/*************************************************************************
* Copyright (c) 2025 Omnineura AI Technologies Pvt Limited.
* All rights reserved.
* This code is licensed under the 'Omnineura AI License' found in the LICENSE file.
* Unauthorized copying, sharing, or use of this code is prohibited.
* The intellectual and technical concepts are proprietary to Omnineura AI Technologies Pvt Limited.
* Author: surya@Omnineura.ai
* Date: 11-09-2025
*************************************************************************/

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  createApiKeyApi,
  listApiKeysApi,
  revokeApiKeyApi,
  validateApiKeyApi,
} from "../services/api";

// Create API Key
export const createApiKey = createAsyncThunk(
  "apiKeys/create",
  async (keyName, { rejectWithValue }) => {
    try {
      const response = await createApiKeyApi(keyName);
      return response.data; // { success, data, message }
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Failed to create API key" });
    }
  }
);

// List API Keys
export const listApiKeys = createAsyncThunk(
  "apiKeys/list",
  async (_, { rejectWithValue }) => {
    try {
      const response = await listApiKeysApi();
      return response.data; // { success, data: { api_keys: [...] } }
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Failed to fetch API keys" });
    }
  }
);

// Revoke API Key
export const revokeApiKey = createAsyncThunk(
  "apiKeys/revoke",
  async (keyId, { rejectWithValue }) => {
    try {
      const response = await revokeApiKeyApi(keyId);
      return response.data; // { success, data: { key_id }, message }

    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Failed to revoke API key" });
    }
  }
);

// Validate API Key
export const validateApiKey = createAsyncThunk(
  "apiKeys/validate",
  async (apiKey, { rejectWithValue }) => {
    try {
      const response = await validateApiKeyApi(apiKey);
      return response.data; // { success, data: {...}, message }
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Failed to validate API key" });
    }
  }
);

const apiKeyReducer = createSlice({
  name: "apiKeys",
  initialState: {
    apiKeys: [],
    loading: false,
    error: null,
    validationResult: null,
    revokeLoading: {},
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearValidationResult: (state) => {
      state.validationResult = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create
      .addCase(createApiKey.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createApiKey.fulfilled, (state, action) => {
        state.loading = false;
        state.apiKeys.push(action.payload.data); // add new key
      })
      .addCase(createApiKey.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message;
      })

      // List
      .addCase(listApiKeys.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(listApiKeys.fulfilled, (state, action) => {
        state.loading = false;
        state.apiKeys = action.payload.api_keys;
      })
      .addCase(listApiKeys.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message;
      })

      // Revoke
      .addCase(revokeApiKey.fulfilled, (state, action) => {
        // console.log(action.payload)
        state.apiKeys = state.apiKeys.filter(
          (key) => key.id !== action.payload.key_id
        );
      })
      .addCase(revokeApiKey.rejected, (state, action) => {
        state.error = action.payload?.message;
      })

      // Validate
      .addCase(validateApiKey.fulfilled, (state, action) => {
        state.validationResult = action.payload.data;
      })
      .addCase(validateApiKey.rejected, (state, action) => {
        state.validationResult = null;
        state.error = action.payload?.message;
      });
  },
});

export const { clearError, clearValidationResult } = apiKeyReducer.actions;
export default apiKeyReducer.reducer;
